import React from 'react';
import styled, { css } from 'styled-components';

export type ShellNavItem = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  href?: string;
  active?: boolean;
  onClick?: () => void;
  badge?: React.ReactNode;
};

export type ShellNavGroup = {
  label?: string;
  items: ShellNavItem[];
};

const SHELL_SIDEBAR_WIDTH = 248;
const SHELL_TOPBAR_HEIGHT = 56;

const Shell = styled.div`
  display: grid;
  grid-template-columns: ${SHELL_SIDEBAR_WIDTH}px 1fr;
  grid-template-rows: ${SHELL_TOPBAR_HEIGHT}px 1fr;
  grid-template-areas:
    'sidebar topbar'
    'sidebar main';
  min-height: 100vh;
  background: var(--color-bg);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      'topbar'
      'main';
  }
`;

const SidebarRoot = styled.aside`
  grid-area: sidebar;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-subtle);
  border-right: 1px solid var(--color-border-subtle);
  padding: var(--space-5) var(--space-3);
  gap: var(--space-4);
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const BrandLogo = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  background: var(--color-accent);
  color: var(--color-text-inverse);
  font-family: var(--font-display);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  flex-shrink: 0;
`;

const BrandTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const BrandTitle = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BrandSubtitle = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const NavSectionLabel = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: var(--space-2) var(--space-2) 2px;
`;

const NavLinkButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-2);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  text-align: left;
  cursor: pointer;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    background: var(--color-surface);
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-accent-subtle);
      color: var(--color-accent-text);
      font-weight: var(--font-weight-semibold);
      &:hover {
        background: var(--color-accent-subtle);
        color: var(--color-accent-text);
      }
    `}
`;

const NavLinkLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NavLinkBadge = styled.span`
  display: inline-flex;
  align-items: center;
`;

const NavLinkIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;

const TopBarRoot = styled.header`
  grid-area: topbar;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: 0 var(--space-6);
  border-bottom: 1px solid var(--color-border-subtle);
  background: var(--color-bg);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TopBarCenter = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--space-3);
`;

const TopBarActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
`;

const Main = styled.main`
  grid-area: main;
  padding: var(--space-8) var(--space-6) var(--space-16);
  overflow-x: hidden;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Sidebar: React.FC<{
  logo?: React.ReactNode;
  brand?: { title: string; subtitle?: string };
  navGroups: ShellNavGroup[];
}> = ({ logo, brand, navGroups }) => {
  return (
    <SidebarRoot>
      {(logo || brand) && (
        <BrandBlock>
          {logo ?? (
            <BrandLogo>
              {(brand?.title ?? 'N').slice(0, 1).toUpperCase()}
            </BrandLogo>
          )}
          {brand && (
            <BrandTextBlock>
              <BrandTitle>{brand.title}</BrandTitle>
              {brand.subtitle && <BrandSubtitle>{brand.subtitle}</BrandSubtitle>}
            </BrandTextBlock>
          )}
        </BrandBlock>
      )}

      {navGroups.map((group, gi) => (
        <NavSection key={group.label ?? `group-${gi}`}>
          {group.label && <NavSectionLabel>{group.label}</NavSectionLabel>}
          {group.items.map((item) => (
            <NavLinkButton
              key={item.id}
              type="button"
              $active={Boolean(item.active)}
              onClick={item.onClick}
              aria-current={item.active ? 'page' : undefined}
            >
              {item.icon && <NavLinkIcon>{item.icon}</NavLinkIcon>}
              <NavLinkLabel>{item.label}</NavLinkLabel>
              {item.badge && <NavLinkBadge>{item.badge}</NavLinkBadge>}
            </NavLinkButton>
          ))}
        </NavSection>
      ))}
    </SidebarRoot>
  );
};

export type AppShellProps = {
  logo?: React.ReactNode;
  brand?: { title: string; subtitle?: string };
  navGroups: ShellNavGroup[];
  topBarCenter?: React.ReactNode;
  topBarActions?: React.ReactNode;
  children: React.ReactNode;
};

export const AppShell: React.FC<AppShellProps> = ({
  logo,
  brand,
  navGroups,
  topBarCenter,
  topBarActions,
  children,
}) => {
  return (
    <Shell>
      <Sidebar logo={logo} brand={brand} navGroups={navGroups} />
      <TopBarRoot>
        <TopBarCenter>{topBarCenter}</TopBarCenter>
        <TopBarActions>{topBarActions}</TopBarActions>
      </TopBarRoot>
      <Main>
        <ContentContainer>{children}</ContentContainer>
      </Main>
    </Shell>
  );
};
